import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Card } from '../components/ui/card';
import { AIBotWidget } from '../components/AIBotWidget';
import {
  Stethoscope, Brain, Pill, HandHeart, Clock, MapPin, CalendarX,
  ShieldCheck, CalendarCheck2, User, Globe, AlarmClock, Plus, Lock, ClipboardCheck, Shield, Check, Zap, BadgeCheck, Sparkles, ArrowLeft, ArrowRight
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Render numerals using Arabic-Indic digits (١٢٣٤) in Arabic mode, plain ASCII otherwise.
const toArabicNum = (n, lang) => {
  if (lang !== 'ar') return String(n);
  const map = ['٠', '١', '٢', '٣', '٤', '٥', '٦', '٧', '٨', '٩'];
  return String(n).split('').map((d) => map[Number(d)] ?? d).join('');
};

// User-provided doctor photo for the consultation card.
const DOCTOR_PHOTO =
  'https://customer-assets.emergentagent.com/job_medical-roles-hub/artifacts/yr99jsa5_WhatsApp%20Image%202026-06-12%20at%2012.20.01%20AM.jpeg';

export const HomePage = () => {
  const { t, language, setLanguage } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [banners, setBanners] = useState([]);
  const [homepage, setHomepage] = useState(null);

  useEffect(() => {
    axios.get(`${API}/categories`).then(r => setCategories(r.data)).catch(() => {});
    axios.get(`${API}/banners`).then(r => setBanners(r.data || [])).catch(() => {});
    axios.get(`${API}/homepage`).then(r => setHomepage(r.data || null)).catch(() => {});
  }, []);

  // Localized helper for bilingual CMS fields (falls back to default string
  // when the admin hasn't set a value yet). `field` is a {ar, en} object.
  const cms = (field, fallbackAr, fallbackEn, fallbackFr) => {
    if (field && typeof field === 'object') {
      const val = field[language] || field.en || field.ar;
      if (val && String(val).trim()) return val;
    }
    return t(fallbackAr, fallbackEn, fallbackFr || fallbackEn);
  };

  const SERVICE_META = {
    doctors: {
      icon: Stethoscope,
      name: { ar: 'طبيب عام', en: 'General Doctor', fr: 'Médecin' },
      route: '/doctors?category=doctors',
    },
    physiotherapy: {
      icon: HandHeart,
      name: { ar: 'علاج طبيعي', en: 'Physiotherapy', fr: 'Physio' },
      route: '/physio/packages',
    },
    psychology: {
      icon: Brain,
      name: { ar: 'استشارة نفسية', en: 'Psychology', fr: 'Psychologie' },
      route: '/psychology',
    },
    pharmacy: {
      icon: Pill,
      name: { ar: 'استشارة صيدلي', en: 'Pharmacy', fr: 'Pharmacie' },
      route: '/doctors?category=pharmacy',
    },
  };

  const handleServiceClick = (cat) => {
    if (cat.status === 'coming_soon') return;
    const route = SERVICE_META[cat.id]?.route;
    if (!route) return;
    // Visitors must create an account before browsing doctors / pharmacists /
    // physiotherapists. Guests are redirected to the register page with a
    // `next` param so they land back on the requested category after signup.
    if (!user) {
      toast.info(
        t('يرجى إنشاء حساب لعرض هذه الخدمة',
          'Please create an account to view this service',
          'Veuillez créer un compte pour accéder à ce service')
      );
      navigate(`/register?next=${encodeURIComponent(route)}`);
      return;
    }
    navigate(route);
  };

  const isRTL = language === 'ar';
  const getLocalized = (obj) => obj?.[language] || obj?.en;

  const profileRoute = () => {
    if (!user) return '/login';
    switch (user.role) {
      case 'admin': return '/admin';
      case 'doctor': return '/doctor';
      case 'pharmacist': return '/pharmacist';
      default: return '/patient';
    }
  };

  // 4 feature pills shown in the 2-column grid right under the header band.
  const featurePills = [
    { icon: Clock,      label: t('في أي وقت',     'Anytime',         'À tout moment') },
    { icon: MapPin,     label: t('من أي مكان',    'From Anywhere',   'De partout') },
    { icon: CalendarX,  label: t('لا مواعيد',     'No Appointments', 'Sans rendez-vous') },
    { icon: ShieldCheck,label: t('خصوصية وأمان',  'Privacy & Safety','Confidentialité & Sécurité') },
  ];

  return (
    <div
      className={`min-h-screen tababa-bg ${isRTL ? 'rtl' : 'ltr'}`}
      dir={isRTL ? 'rtl' : 'ltr'}
      data-testid="home-page"
    >
      {/* ===== Compact header band ===== */}
      <header className="tababa-header text-white shadow-md" data-testid="home-header">
        <div className="max-w-5xl mx-auto px-4 h-16 flex items-center justify-between">
          {/* Logo + brand on the RTL start side */}
          <Link to="/" className="flex items-center gap-2" data-testid="home-brand">
            <span
              className="text-2xl md:text-3xl font-extrabold tracking-tight"
              style={{ fontFamily: '"Tajawal","Cairo",sans-serif' }}
            >
              {t('طبابة', 'Tababa', 'Tababa')}
            </span>
            <img
              src="/images/logo.png"
              alt="Tababa"
              className="h-10 w-10 rounded-full bg-white p-0.5 shadow-sm"
            />
          </Link>

          {/* Small icon group on the opposite side */}
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => {
                const order = ['ar', 'en', 'fr'];
                const next = order[(order.indexOf(language) + 1) % order.length];
                setLanguage(next);
              }}
              className="w-8 h-8 rounded-full bg-white/15 hover:bg-white/25 active:scale-95 flex items-center justify-center transition"
              data-testid="home-lang-toggle"
              aria-label="language"
            >
              <Globe size={16} />
            </button>
            <Link
              to={profileRoute()}
              className="w-8 h-8 rounded-full bg-white/15 hover:bg-white/25 active:scale-95 flex items-center justify-center transition"
              data-testid="home-profile-link"
              aria-label="profile"
            >
              <User size={16} />
            </Link>
          </div>
        </div>
      </header>

      {/* ===== Page body ===== */}
      <main className="max-w-5xl mx-auto px-4 pt-6 pb-12 space-y-10">
        {/* Consultation hero — modern centered focal block */}
        <Card
          className="relative overflow-hidden border-0 rounded-[28px] bg-white"
          style={{ boxShadow: '0 14px 40px -18px rgba(13, 71, 161, 0.25)' }}
          data-testid="consultation-hero-card"
        >
          {/* Soft tinted background accent */}
          <div
            aria-hidden="true"
            className="absolute inset-0 pointer-events-none"
            style={{
              background:
                'radial-gradient(110% 70% at 100% 0%, rgba(34,200,184,0.10) 0%, transparent 55%), radial-gradient(110% 70% at 0% 100%, rgba(21,101,192,0.10) 0%, transparent 55%)',
            }}
          />

          <div className={`relative grid md:grid-cols-5 gap-6 items-center p-5 md:p-9 ${isRTL ? 'text-right' : 'text-left'}`}>
            {/* Doctor avatar */}
            <div className="md:col-span-2 flex justify-center">
              <div className="relative">
                <div
                  aria-hidden="true"
                  className="absolute inset-0 rounded-full blur-2xl opacity-60"
                  style={{ background: 'radial-gradient(closest-side, rgba(34,200,184,.35), transparent 70%)' }}
                />
                <img
                  src={homepage?.hero?.doctor_photo_url || DOCTOR_PHOTO}
                  alt="doctor"
                  onError={(e) => { e.currentTarget.style.display = 'none'; }}
                  className="relative w-40 h-40 md:w-52 md:h-52 rounded-full object-cover ring-8 ring-white shadow-xl"
                />
                <span className="absolute bottom-1 end-1 w-11 h-11 rounded-full bg-gradient-to-br from-[#22c8b8] to-[#1565c0] text-white flex items-center justify-center shadow-md">
                  <CalendarCheck2 size={20} strokeWidth={2} />
                </span>
              </div>
            </div>

            {/* Text + CTA */}
            <div className={`md:col-span-3 flex flex-col ${isRTL ? 'items-end' : 'items-start'}`}>
              <h1 className="text-3xl md:text-5xl font-black text-[#0d2c54] leading-tight" data-testid="hero-title">
                {cms(homepage?.hero?.title,
                  'ابدأ استشارتك الطبية الآن',
                  'Start your medical consultation now',
                  'Commencez votre consultation médicale maintenant')}
              </h1>
              <p className="mt-3 text-gray-500 text-base md:text-lg max-w-md" data-testid="hero-subtitle">
                {cms(homepage?.hero?.subtitle,
                  'احجز الباقة المناسبة لك',
                  'Pick the package that suits you',
                  'Choisissez le forfait qui vous convient')}
              </p>

              <Link to={user ? (homepage?.hero?.cta_link || '/subscriptions') : '/login'} className="mt-6 inline-block w-full md:w-auto" data-testid="consult-cta-link">
                <button
                  type="button"
                  className="tababa-cta-pill w-full md:w-auto px-9 h-14 rounded-full text-white font-extrabold text-base md:text-lg shadow-lg active:scale-[0.98] transition"
                  data-testid="consult-cta-button"
                >
                  {cms(homepage?.hero?.cta_text,
                    'تواصل مع طبيب', 'Talk to a Doctor', 'Parlez à un médecin')}
                </button>
              </Link>

              {/* Inline trust chips (replaces the old 2×2 grid above) */}
              <div className="mt-6 flex flex-wrap gap-2" data-testid="feature-pills">
                {featurePills.map((p) => {
                  const Icon = p.icon;
                  return (
                    <div
                      key={p.label}
                      className="trust-chip inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#eaf2fa] text-[#0d47a1] text-xs md:text-sm font-semibold"
                      data-testid={`feature-pill-${p.label}`}
                    >
                      <Icon size={14} strokeWidth={2} className="text-[#1565c0]" />
                      {p.label}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </Card>

        {/* Admin-managed banners (CMS) — merged from legacy flat banners AND
            new bilingual banners in `homepage.banners`. Bilingual first. */}
        {(() => {
          const cmsBanners = (homepage?.banners || [])
            .filter((b) => b.active)
            .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
            .map((b) => ({
              _id: b.id,
              title: cms(b.title, '', '', ''),
              subtitle: cms(b.subtitle, '', '', ''),
              image_url: b.image_url,
              cta_link: b.cta_link,
            }));
          const legacy = banners.map((b) => ({
            _id: b._id,
            title: b.title,
            subtitle: b.subtitle,
            image_url: b.image_url,
            cta_link: b.cta_link,
          }));
          const allBanners = [...cmsBanners, ...legacy];
          if (allBanners.length === 0) return null;
          const renderBannerBody = (b) => (
            <>
              {b.image_url ? (
                <div className="relative">
                  <img src={b.image_url} alt={b.title || ''} className="w-full h-40 md:h-52 object-cover" />
                  {(b.title || b.subtitle) && (
                    <div className={`absolute inset-0 flex flex-col justify-end p-4 bg-gradient-to-t from-black/60 via-black/15 to-transparent text-white ${isRTL ? 'text-right items-end' : 'text-left items-start'}`}>
                      {b.title && <h3 className="text-lg md:text-xl font-extrabold drop-shadow">{b.title}</h3>}
                      {b.subtitle && <p className="text-xs md:text-sm opacity-95 mt-0.5">{b.subtitle}</p>}
                    </div>
                  )}
                </div>
              ) : (
                <div className="bg-gradient-to-l from-[#1976d2] to-[#0d47a1] p-5 text-white">
                  {b.title && <h3 className="text-lg font-extrabold">{b.title}</h3>}
                  {b.subtitle && <p className="text-sm mt-1 text-blue-100">{b.subtitle}</p>}
                </div>
              )}
            </>
          );
          return (
            <div className="space-y-3" data-testid="dynamic-banners">
              {allBanners.map((b) => (
                <Card key={b._id} className="relative overflow-hidden border-0 shadow-md rounded-2xl" data-testid={`banner-${b._id}`}>
                  {b.cta_link ? (
                    <Link to={b.cta_link} className="block">{renderBannerBody(b)}</Link>
                  ) : renderBannerBody(b)}
                </Card>
              ))}
            </div>
          );
        })()}

        {/* Services section — bigger, more spacious cards */}
        <section data-testid="services-section">
          <div className={`mb-5 ${isRTL ? 'text-right' : 'text-left'}`}>
            <h2 className="inline-block text-2xl md:text-3xl font-extrabold text-[#0d2c54] pb-1.5 border-b-[3px] border-[#1e88e5]" data-testid="services-heading">
              {cms(homepage?.sections?.services_heading, 'الخدمات', 'Services', 'Services')}
            </h2>
          </div>

          <div className="grid grid-cols-2 gap-4 md:gap-5" data-testid="services-grid">
            {categories.map((cat) => {
              const meta = SERVICE_META[cat.id];
              if (!meta) return null;
              const Icon = meta.icon;
              const disabled = cat.status === 'coming_soon';
              return (
                <button
                  key={cat.id}
                  type="button"
                  onClick={() => handleServiceClick(cat)}
                  disabled={disabled}
                  data-testid={`category-${cat.id}`}
                  className={`tababa-service-card-modern flex flex-col items-center text-center px-4 py-7 md:py-9 transition-all
                    ${disabled ? 'opacity-60 cursor-not-allowed' : 'hover:-translate-y-1 cursor-pointer'}`}
                >
                  <span className="w-20 h-20 md:w-24 md:h-24 rounded-2xl bg-gradient-to-br from-[#e3f2fd] to-[#d6ebf7] text-[#1565c0] flex items-center justify-center mb-4 shadow-inner">
                    <Icon size={36} strokeWidth={1.6} />
                  </span>
                  <h3 className="text-base md:text-lg font-extrabold text-[#0d2c54]">
                    {getLocalized(meta.name)}
                  </h3>
                  {disabled && (
                    <p className="text-xs text-gray-400 mt-1">
                      {t('قريباً', 'Coming Soon', 'Bientôt')}
                    </p>
                  )}
                </button>
              );
            })}
          </div>
        </section>

        {/* ===== Subscription CTA banner — under categories ===== */}
        {(homepage?.offer?.enabled !== false) && (
        <section data-testid="subscription-cta-banner">
          <Link
            to={homepage?.offer?.cta_link || '/subscriptions'}
            data-testid="subscription-cta-banner-link"
            className="group relative block overflow-hidden rounded-3xl shadow-xl
                       bg-gradient-to-br from-[#0d47a1] via-[#1565c0] to-[#22c8b8]
                       hover:shadow-2xl hover:-translate-y-0.5 transition-all duration-300"
          >
            {/* Decorative blobs */}
            <span aria-hidden="true"
                  className="absolute -top-12 -right-12 w-48 h-48 rounded-full bg-white/10 blur-2xl" />
            <span aria-hidden="true"
                  className="absolute -bottom-16 -left-10 w-56 h-56 rounded-full bg-[#22c8b8]/30 blur-3xl" />

            <div className={`relative flex items-center justify-between gap-4 p-6 md:p-8 text-white
                            ${isRTL ? 'flex-row-reverse text-right' : 'text-left'}`}>
              <div className="flex-1 min-w-0">
                <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/15 backdrop-blur-sm mb-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <Sparkles size={14} className="text-yellow-300" />
                  <span className="text-[11px] md:text-xs font-extrabold tracking-wider uppercase" data-testid="offer-badge">
                    {cms(homepage?.offer?.badge, 'عرض حصري', 'Exclusive offer', 'Offre exclusive')}
                  </span>
                </div>

                <h3 className="text-xl md:text-3xl font-extrabold leading-snug drop-shadow-sm" data-testid="offer-headline">
                  {cms(homepage?.offer?.headline,
                     'اشترك الآن وابدأ رحلتك الصحية',
                     'Subscribe now and start your health journey',
                     'Abonnez-vous et commencez votre parcours santé')}
                </h3>

                <p className="mt-2 text-sm md:text-base text-blue-50/95" data-testid="offer-description">
                  {cms(homepage?.offer?.description,
                     'باقة طبابة بـ $20/شهر فقط — وفّر $10!',
                     'Tababa Package for just $20/month — save $10!',
                     'Forfait Tababa à seulement 20$/mois — économisez 10$ !')}
                </p>

                <div className={`mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-full
                                bg-white text-[#0d47a1] font-bold text-sm md:text-base
                                group-hover:gap-3 transition-all shadow-md ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <span data-testid="offer-cta-text">
                    {cms(homepage?.offer?.cta_text, 'احصل على الباقة', 'Grab your package', 'Obtenir le forfait')}
                  </span>
                  {isRTL ? <ArrowLeft size={18} /> : <ArrowRight size={18} />}
                </div>
              </div>

              {/* Right side price badge (hidden on small screens) */}
              <div className="hidden md:flex flex-col items-center justify-center flex-shrink-0
                              w-32 h-32 rounded-2xl bg-white/15 backdrop-blur-md border border-white/25">
                <span className="text-xs uppercase tracking-wider opacity-90">
                  {cms(homepage?.offer?.only_label, 'فقط', 'Only', 'Seulement')}
                </span>
                <span className="text-4xl font-extrabold leading-none mt-1" data-testid="offer-price">
                  {homepage?.offer?.price || '$20'}
                </span>
                {(homepage?.offer?.original_price || '$30') && (
                  <span className="text-xs opacity-90 mt-1 line-through">{homepage?.offer?.original_price || '$30'}</span>
                )}
                <span className="text-[10px] uppercase tracking-wider mt-1 opacity-90">
                  /{cms(homepage?.offer?.period_label, 'شهر', 'month', 'mois')}
                </span>
              </div>
            </div>
          </Link>
        </section>
        )}

        {/* ===== Why Tababa? ===== */}
        <section className="why-tababa-wrap pt-12 pb-10 px-4 md:px-8" data-testid="why-tababa-section">
          {/* Header with accent line */}
          <div className={`mb-10 ${isRTL ? 'text-right' : 'text-left'}`}>
            <div className={`inline-flex items-center gap-3 mb-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <span className="w-12 h-1 rounded-full bg-gradient-to-r from-[#22c8b8] to-[#1565c0]" />
              <span className="text-xs font-extrabold tracking-[0.18em] text-[#1565c0] uppercase">
                {t('طبابة', 'Tababa', 'Tababa')}
              </span>
            </div>
            <h2 className="why-title text-4xl md:text-5xl tracking-tight" data-testid="why-heading">
              {cms(homepage?.sections?.why_heading, 'لماذا طبابة؟', 'Why Tababa?', 'Pourquoi Tababa ?')}
            </h2>
            <p className="why-subtitle text-lg md:text-xl mt-2" data-testid="why-subtitle">
              {cms(homepage?.sections?.why_subtitle,
                'أسرع وأنسب طريقة لاستشارة طبيب:',
                'The fastest, most convenient way to consult a doctor:',
                "Le moyen le plus rapide pour consulter un médecin :")}
            </p>
          </div>

          {/* Alternating-row feature list */}
          <div className="space-y-6 md:space-y-8" data-testid="why-tababa-grid">
            {[
              {
                num: 1,
                tint: 'from-[#22c8b8] to-[#1aa0d4]',
                tintSoft: 'from-[#e6fbf7] to-[#e6f5fd]',
                icon: (
                  <Zap className="w-14 h-14 md:w-16 md:h-16" strokeWidth={1.6} fill="currentColor" fillOpacity={0.12} />
                ),
                title: t('وصول فوري', 'Instant Access', 'Accès instantané'),
                desc:  t('استشر طبيباً خلال دقائق، بلا انتظار ولا مواعيد.',
                         'Consult a doctor within minutes — no waiting, no appointments.',
                         "Consultez un médecin en quelques minutes, sans attente.")
              },
              {
                num: 2,
                tint: 'from-[#1aa0d4] to-[#1565c0]',
                tintSoft: 'from-[#e6f5fd] to-[#e3f2fd]',
                icon: (
                  <div className="relative w-14 h-14 md:w-16 md:h-16">
                    <AlarmClock className="w-full h-full" strokeWidth={1.7} />
                    <span className="absolute -bottom-1 -left-1 flex items-center font-extrabold text-base md:text-lg">
                      <Plus size={14} strokeWidth={3} className="-mr-0.5" />24
                    </span>
                  </div>
                ),
                title: t('متوفرون دائماً', 'Always Available', 'Toujours disponibles'),
                desc:  t('معك بلا توقف. رعاية طبية متوفرة على مدار الساعة.',
                         'With you nonstop. Medical care available 24/7.',
                         "Avec vous sans interruption, 24h/24.")
              },
              {
                num: 3,
                tint: 'from-[#1565c0] to-[#0d47a1]',
                tintSoft: 'from-[#e3f2fd] to-[#dbeaf7]',
                icon: (
                  <div className="relative w-14 h-14 md:w-16 md:h-16 flex items-center justify-center">
                    <Shield className="w-full h-full" strokeWidth={1.7} />
                    <Lock className="absolute" size={22} strokeWidth={2.2} style={{ top: '38%' }} />
                  </div>
                ),
                title: t('خصوصية وأمان', 'Privacy & Security', 'Confidentialité & Sécurité'),
                desc:  t('بياناتك الصحية خط أحمر، محمية بأعلى معايير الأمان والسرية.',
                         'Your health data is sacred — protected by the highest security and confidentiality standards.',
                         "Vos données de santé sont protégées par les normes les plus strictes.")
              },
              {
                num: 4,
                tint: 'from-[#0d47a1] to-[#22c8b8]',
                tintSoft: 'from-[#dbeaf7] to-[#e6fbf7]',
                icon: (
                  <div className="relative w-14 h-14 md:w-16 md:h-16 flex items-center justify-center">
                    <Stethoscope className="w-full h-full" strokeWidth={1.7} />
                    <span className="absolute -bottom-1 -right-1 w-6 h-6 md:w-7 md:h-7 rounded-full bg-white text-[#1565c0] flex items-center justify-center shadow">
                      <BadgeCheck size={18} strokeWidth={1.8} fill="#e3f2fd" />
                    </span>
                  </div>
                ),
                title: t('أطباء معتمدون', 'Certified Doctors', 'Médecins certifiés'),
                desc:  t('نضمن لك استشارة موثوقة من أول مرة، مع أطباء مختارين بعناية.',
                         'A trusted consultation from the first time — with carefully selected doctors.',
                         "Une consultation fiable dès la première fois, avec des médecins sélectionnés.")
              }
            ].map((item, idx) => {
              // Alternate row direction for visual rhythm (in RTL the swap happens visually)
              const reverse = idx % 2 === 1;
              return (
                <div
                  key={item.num}
                  className={`why-row group relative grid grid-cols-[88px_1fr] md:grid-cols-[120px_1fr] gap-4 md:gap-6 items-center p-4 md:p-6 rounded-3xl bg-gradient-to-br ${item.tintSoft}
                    ${reverse ? 'md:[direction:rtl]' : ''} ${isRTL ? 'text-right' : 'text-left'}`}
                  style={{ direction: isRTL ? 'rtl' : 'ltr' }}
                  data-testid={`why-item-${item.num}`}
                >
                  {/* Icon tile */}
                  <div className={`relative aspect-square w-20 h-20 md:w-28 md:h-28 rounded-2xl bg-white shadow-md flex items-center justify-center text-transparent bg-clip-padding`}>
                    <div className={`absolute inset-0 rounded-2xl bg-gradient-to-br ${item.tint} opacity-10`} />
                    <div className={`relative bg-gradient-to-br ${item.tint} bg-clip-text text-transparent`}
                         style={{ WebkitTextFillColor: 'transparent', color: 'transparent' }}>
                      <div className="text-[#1565c0]" style={{ color: 'unset' }}>
                        {/* fall-back solid color icon (gradient text-clip on SVG strokes is unreliable) */}
                      </div>
                    </div>
                    <div className="absolute inset-0 flex items-center justify-center text-[#1565c0]">
                      {item.icon}
                    </div>
                    {/* Number chip */}
                    <span className={`absolute -top-2 ${isRTL ? '-left-2' : '-right-2'} w-7 h-7 rounded-full bg-gradient-to-br ${item.tint} text-white text-xs font-extrabold flex items-center justify-center shadow`}>
                      {toArabicNum(item.num, language)}
                    </span>
                  </div>

                  {/* Text */}
                  <div className={isRTL ? 'text-right' : 'text-left'}>
                    <h3 className="why-item-title text-xl md:text-2xl mb-1.5">
                      {item.title}
                    </h3>
                    <p className="why-item-desc text-sm md:text-base leading-relaxed">
                      {item.desc}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      </main>

      {/* ===== Footer ===== */}
      <footer className="bg-white border-t border-gray-100 mt-4" data-testid="home-footer">
        <div className="max-w-5xl mx-auto px-6 py-8">
          <div className={`grid grid-cols-2 gap-6 ${isRTL ? 'text-right' : 'text-left'}`}>
            <div data-testid="footer-col-brand">
              <h4 className="text-base md:text-lg font-extrabold text-[#0d2c54] mb-3">
                {t('طبابة', 'Tababa', 'Tababa')}
              </h4>
              <ul className="space-y-2 text-sm text-gray-700">
                <li><Link to="/about" className="hover:text-[#1565c0] transition" data-testid="footer-about">{t('من نحن', 'About Us', 'À propos')}</Link></li>
                <li><Link to="/legal/privacy" className="hover:text-[#1565c0] transition" data-testid="footer-privacy">{t('سياسة الخصوصية', 'Privacy Policy', 'Confidentialité')}</Link></li>
                <li><Link to="/legal/terms" className="hover:text-[#1565c0] transition" data-testid="footer-terms">{t('شروط الاستخدام', 'Terms of Use', "Conditions d'utilisation")}</Link></li>
                <li><Link to="/contact" className="hover:text-[#1565c0] transition" data-testid="footer-contact">{t('تواصل معنا', 'Contact Us', 'Contactez-nous')}</Link></li>
              </ul>
            </div>

            <div data-testid="footer-col-join">
              <h4 className="text-base md:text-lg font-extrabold text-[#0d2c54] mb-3">
                {t('انضم إلى طبابة', 'Join Tababa', 'Rejoindre Tababa')}
              </h4>
              <ul className="space-y-2 text-sm text-gray-700">
                <li><Link to="/apply/doctor" className="hover:text-[#1565c0] transition" data-testid="footer-join-doctor">{t('دكتور أو صيدلي', 'Doctor or Pharmacist', 'Médecin ou Pharmacien')}</Link></li>
                <li><Link to="/apply/jobs" className="hover:text-[#1565c0] transition" data-testid="footer-careers">{t('الوظائف', 'Careers', 'Carrières')}</Link></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-100 mt-6 pt-5 flex flex-wrap items-center justify-center gap-x-3 gap-y-2 text-sm" data-testid="footer-lang-row">
            <button type="button" onClick={() => setLanguage('ar')}
              className={`transition ${language === 'ar' ? 'text-[#0d2c54] font-bold' : 'text-gray-500 hover:text-[#0d2c54]'}`}
              data-testid="footer-lang-ar">العربي</button>
            <span className="text-gray-300">|</span>
            <button type="button" onClick={() => setLanguage('en')}
              className={`transition ${language === 'en' ? 'text-[#0d2c54] font-bold' : 'text-gray-500 hover:text-[#0d2c54]'}`}
              data-testid="footer-lang-en">English</button>
            <span className="text-gray-300">|</span>
            <button type="button" onClick={() => setLanguage('fr')}
              className={`transition ${language === 'fr' ? 'text-[#0d2c54] font-bold' : 'text-gray-500 hover:text-[#0d2c54]'}`}
              data-testid="footer-lang-fr">Français</button>
          </div>

          <p className="text-center text-xs text-gray-400 mt-4" data-testid="footer-copyright">
            {t('2026 طبابة. جميع الحقوق محفوظة.',
              '© 2026 Tababa. All rights reserved.',
              '© 2026 Tababa. Tous droits réservés.')}
          </p>
        </div>
      </footer>

      <style>{`
        .tababa-bg {
          background:
            radial-gradient(120% 60% at 0% 100%, #dbeaf7 0%, transparent 55%),
            radial-gradient(120% 60% at 100% 0%, #e6f1fb 0%, transparent 55%),
            #f4f8fc;
        }
        .tababa-header {
          background: linear-gradient(180deg, #1f6cb5 0%, #1565c0 60%, #14579e 100%);
        }
        .tababa-pill {
          background: #eaf2fa;
          border-radius: 9999px;
          box-shadow: 0 1px 2px rgba(15, 64, 130, .04);
        }
        .tababa-cta-pill {
          background: linear-gradient(95deg, #22c8b8 0%, #1aa0d4 55%, #1565c0 100%);
          box-shadow: 0 6px 16px rgba(26, 160, 212, .35);
        }
        .tababa-service-card {
          background: #ffffff;
          border-radius: 18px;
          box-shadow: 0 2px 6px rgba(15, 64, 130, .06);
        }
        /* Modern service card variant — softer, more spacious */
        .tababa-service-card-modern {
          background: #ffffff;
          border-radius: 22px;
          border: 1px solid #eef4fb;
          box-shadow: 0 6px 22px -12px rgba(13, 71, 161, .18);
        }
        .tababa-service-card-modern:hover:not(:disabled) {
          box-shadow: 0 14px 30px -16px rgba(13, 71, 161, .28);
          border-color: #d6ebf7;
        }
        .trust-chip {
          backdrop-filter: blur(6px);
          transition: transform .15s ease;
        }
        .trust-chip:hover { transform: translateY(-1px); }

        /* ===== Why Tababa? section — white card, Tababa branding ===== */
        .why-tababa-wrap {
          background: #ffffff;
          border-radius: 28px;
          color: #0d2c54;
          box-shadow: 0 4px 18px rgba(15, 64, 130, .06);
          border: 1px solid #eaf2fa;
        }
        .why-title {
          color: #0d2c54;
          font-weight: 900;
          font-family: "Tajawal","Cairo","Almarai",sans-serif;
        }
        .why-subtitle {
          color: #355170;
          font-weight: 600;
          font-family: "Tajawal","Cairo","Almarai",sans-serif;
        }
        .why-icon { color: #1e88e5; }            /* Tababa blue line icons */
        .why-item-title {
          color: #0d2c54;
          font-weight: 900;
          font-family: "Tajawal","Cairo","Almarai",sans-serif;
          letter-spacing: -0.01em;
        }
        .why-item-desc {
          color: #4a5b73;
          font-weight: 500;
          font-family: "Tajawal","Cairo","Almarai",sans-serif;
        }
      `}</style>
      <AIBotWidget />
    </div>
  );
};

export default HomePage;
